<template>
  <section class="overflow-y-hidden h-screen">
    <section class="p-1 bg-gray-100 h-full flex-1 flex-grow flex-col">
      <section class="flex h-full no-wrap space-x-1">
        <PrMainDrawer />
        <section class="w-full flex-grow space-y-1 flex flex-col">
          <PrMainToolbar class="h-16 w-full bg-white flex-shrink-0 shadow-sm" />
          <!-- CONTENT -->
          <!-- A light grey ground from the theme, so white surfaces (tiles, edit panels, lists) stand out. -->
          <section
            class="bg-surface-50 dark:bg-surface-950 w-full flex-1 min-h-0 flex flex-col justify-between border-solid border rounded-xl border-gray-300"
            data-page-ground
          >
            <section class="flex-1 min-h-0 flex flex-col">
              <MfaRecommendedBanner
                :visible="showMfaNudgeBanner"
                @setup="handleMfaNudgeSetup"
                @dismiss="handleBannerDismiss"
              />
              <main class="flex-1 min-h-0 flex flex-col p-4 overflow-auto">
                <div class="schedule-content-wrap flex-1 min-h-0 flex flex-col">
                  <ErrorBoundary fallback-message="Failed to load this page. Please try again.">
                    <router-view />
                  </ErrorBoundary>
                </div>
              </main>
            </section>
          </section>

          <MfaRecommendedDialog
            v-model:visible="showMfaNudgeDialog"
            @setup="handleMfaNudgeSetup"
            @dismiss="handleMfaNudgeDismiss"
          />
          <PrFooter />
        </section>
      </section>
    </section>
  </section>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import ErrorBoundary from '@/components/ErrorBoundary.vue';
import PrMainDrawer from '@/components/PrMainDrawer.vue';
import PrMainToolbar from '@/components/PrMainToolbar.vue';
import PrFooter from '@/components/PrFooter.vue';
import { useAuthStore } from '@/stores/authStore';
import { MfaRecommendedDialog, MfaRecommendedBanner } from '@erebus/ui';
import { getRuntimeConfig } from '@erebus/runtime-config';

const authStore = useAuthStore();
const router = useRouter();
const { useCookieAuth } = getRuntimeConfig();

const showMfaNudgeDialog = ref(false);
const showMfaNudgeBanner = ref(false);

watch(
  () => authStore.mfaRecommended,
  (recommended) => {
    if (!useCookieAuth || !recommended) return;
    const dismissed = sessionStorage.getItem('mfa-nudge-dismissed');
    if (!dismissed) {
      showMfaNudgeDialog.value = true;
    } else {
      const bannerDismissed = sessionStorage.getItem('mfa-banner-dismissed');
      if (!bannerDismissed) {
        showMfaNudgeBanner.value = true;
      }
    }
  },
  { immediate: true },
);

function handleMfaNudgeSetup() {
  showMfaNudgeDialog.value = false;
  showMfaNudgeBanner.value = false;
  router.push({ name: 'security' });
}

function handleMfaNudgeDismiss() {
  showMfaNudgeDialog.value = false;
  sessionStorage.setItem('mfa-nudge-dismissed', 'true');
  showMfaNudgeBanner.value = true;
}

function handleBannerDismiss() {
  showMfaNudgeBanner.value = false;
  sessionStorage.setItem('mfa-banner-dismissed', 'true');
}
</script>

<style scoped>
/* Let the view (e.g. ScheduleView) fill the content area so calendar can be full height */
.schedule-content-wrap > :deep(*) {
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
</style>
